// /patient/chart — my chart summary, conditions, last visit summary,
// and the "add a note for the doctor" entry point.
import React from "react";
import { Link } from "react-router-dom";
import {
  doctorLabel,
  firstNameOf,
  formatShortDate,
  usePatientData,
  useUpcomingAndPast,
} from "./PatientDataContext";
import { ConditionsList } from "./patientMockData";

export default function PatientChart() {
  const { userRow, rows, formsByRow } = usePatientData();
  const { past } = useUpcomingAndPast(rows);
  const lastVisit = past[0] || null;
  const doctor = doctorLabel(rows[0] || {});

  const formLinks = [];
  [...past].forEach((row) => {
    (formsByRow[row.id] || []).forEach((url, i) => {
      formLinks.push({ key: `${row.id}-form-${i}`, row, url, index: i + 1 });
    });
  });

  return (
    <>
      <div className="pp-page-head">
        <Link to="." className="pp-back-link">‹ Home</Link>
        <h1 className="pp-page-title">My chart</h1>
        <div className="pp-page-sub">Summary kept by your clinic team</div>
      </div>

      {/* ABOUT YOU */}
      <div className="pp-section-title">About you</div>
      <div className="pp-card">
        <div className="pp-detail-h2">{firstNameOf(userRow)}</div>
        <div className="pp-detail-row">
          <span className="pp-lbl">Name</span>
          <span className="pp-val">
            {userRow?.patient_full_name || userRow?.name || "—"}
          </span>
        </div>
        <div className="pp-detail-row">
          <span className="pp-lbl">Date of birth</span>
          <span className="pp-val">{userRow?.patient_dob || "—"}</span>
        </div>
        <div className="pp-detail-row">
          <span className="pp-lbl">Phone</span>
          <span className="pp-val">{userRow?.patient_phone || "—"}</span>
        </div>
        <div className="pp-detail-row">
          <span className="pp-lbl">Email</span>
          <span className="pp-val">
            {userRow?.patient_email || userRow?.email || "—"}
          </span>
        </div>
        <div className="pp-detail-row">
          <span className="pp-lbl">Record ID</span>
          <span className="pp-val">{userRow?.patient_ehr_id || "—"}</span>
        </div>
        <div className="pp-detail-row">
          <span className="pp-lbl">Primary doctor</span>
          <span className="pp-val">{doctor}</span>
        </div>
        <div className="pp-detail-row">
          <span className="pp-lbl">Allergies</span>
          <span className="pp-val">Penicillin</span>
        </div>
      </div>

      {/* CONDITIONS */}
      <div className="pp-section-title">Conditions your doctor follows</div>
      <ConditionsList onClick={() => null} />

      {/* LAST VISIT SUMMARY */}
      {lastVisit && (
        <>
          <div className="pp-section-title">Last visit summary</div>
          <div className="pp-card">
            <div className="pp-visit-eyebrow" style={{ marginBottom: 4 }}>
              {formatShortDate(lastVisit.date_)} —{" "}
              {lastVisit.type || "annual exam"}
            </div>
            <p className="pp-detail-p">
              <strong>{doctor} wrote:</strong>{" "}
              {lastVisit.note_signed_text ||
                "Visit summary kept by your care team. Open the visit to see the full note."}
            </p>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <Link to="wizard" className="pp-btn pp-ghost">
                ✨ Explain
              </Link>
              <Link to="message" className="pp-btn pp-ghost">
                Ask a question
              </Link>
            </div>
          </div>
        </>
      )}

      {/* MY FORMS */}
      <div className="pp-section-title">My forms</div>
      {formLinks.length === 0 && (
        <div className="pp-empty">No submitted forms yet.</div>
      )}
      {formLinks.map(({ key, row, url, index }) => (
        <a
          key={key}
          className="pp-row"
          href={url}
          target="_blank"
          rel="noopener noreferrer"
        >
          <div className="pp-row-leading pp-amber">📋</div>
          <div className="pp-row-body">
            <div className="pp-row-title">Form submission {index}</div>
            <div className="pp-row-meta">{formatShortDate(row.date_)} visit</div>
          </div>
          <span className="pp-row-chev">↗</span>
        </a>
      ))}

      {/* ADD A NOTE */}
      <div className="pp-section-title">Add to your chart</div>
      <div className="pp-card">
        <p className="pp-detail-p">
          Anything you want {doctor} to know? Notes you add here become part of
          your chart and Norinne reviews them before your next visit.
        </p>
        <Link
          to="message"
          state={{ about: "A general question" }}
          className="pp-btn pp-ghost"
        >
          ＋ Add a note for the doctor
        </Link>
      </div>
    </>
  );
}